import { useState, useEffect, useRef } from 'react';
import { Poster } from '../../types';
import PitchView from './PitchView';
import ChatMessage from './ChatMessage';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAIPitch } from '../../hooks/useAIPitch';
import { useAIChat } from '../../hooks/useAIChat';

interface AISidebarProps {
  poster: Poster;
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'actions' | 'chat' | 'pitch';

export default function AISidebar({ poster, isOpen, onClose }: AISidebarProps) {
  const [activeTab, setActiveTab] = useState<TabType>('actions');
  const [inputValue, setInputValue] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  const { pitch, loading: pitchLoading, error: pitchError, generatePitch } = useAIPitch(poster.id);
  const { messages, loading: chatLoading, sendMessage, clearMessages } = useAIChat(poster.id);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Reset to actions tab when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('actions');
    }
  }, [isOpen]);

  const handleGeneratePitch = async () => {
    try {
      await generatePitch();
      setActiveTab('pitch');
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inputValue.trim() || chatLoading) return;
    
    const question = inputValue.trim();
    setInputValue('');
    setActiveTab('chat');
    await sendMessage(question);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 right-0 h-full w-full sm:w-96 lg:w-[500px]
          bg-white shadow-2xl z-50
          transform transition-transform duration-300 ease-in-out
          flex flex-col
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h2 className="text-xl font-display font-bold text-neutral-900">
            AI Pitch Assistant
          </h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-900 transition-colors"
            aria-label="Close sidebar"
          >
            <span className="text-2xl">×</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-200">
          <button
            onClick={() => setActiveTab('actions')}
            className={`
              flex-1 px-4 py-3 text-sm font-medium transition-colors
              ${activeTab === 'actions'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
              }
            `}
          >
            Quick Actions
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`
              flex-1 px-4 py-3 text-sm font-medium transition-colors
              ${activeTab === 'chat'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
              }
            `}
          >
            Chat
          </button>
          <button
            onClick={() => setActiveTab('pitch')}
            className={`
              flex-1 px-4 py-3 text-sm font-medium transition-colors
              ${activeTab === 'pitch'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-neutral-600 hover:text-neutral-900'
              }
            `}
          >
            Pitch Deck
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'actions' && (
            <div className="p-4 space-y-4 h-full overflow-y-auto">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  Quick Actions
                </h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Transform your scientific poster into an investor-friendly pitch deck
                </p>
              </div>

              <Button
                onClick={handleGeneratePitch}
                disabled={pitchLoading}
                className="w-full"
              >
                {pitchLoading ? 'Generating Pitch...' : 'Generate Investor Pitch'}
              </Button>

              {pitchError && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  {pitchError}
                </div>
              )}

              <div className="pt-4 border-t border-neutral-200">
                <h4 className="text-sm font-semibold text-neutral-900 mb-2">
                  Suggested Questions:
                </h4>
                <div className="space-y-2">
                  {[
                    "What's the commercial potential?",
                    "What are the key risks?",
                    "How does this compare to existing solutions?",
                    "What's the market opportunity?",
                  ].map((question, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setInputValue(question);
                        setActiveTab('chat');
                        setTimeout(() => handleSendMessage(), 100);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-neutral-700 
                                 hover:bg-neutral-50 rounded-lg transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-neutral-500 py-8">
                    <p className="mb-2">Ask me anything about this research!</p>
                    <p className="text-sm">I'll explain it in investor-friendly terms.</p>
                  </div>
                ) : (
                  <>
                    {messages.map((message) => (
                      <ChatMessage key={message.id} message={message} />
                    ))}
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="bg-neutral-100 rounded-lg px-4 py-2">
                          <p className="text-sm text-neutral-600">Thinking...</p>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </>
                )}
              </div>

              <div className="border-t border-neutral-200 p-4">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Ask a question..."
                    disabled={chatLoading}
                    className="flex-1"
                  />
                  <Button
                    type="submit"
                    disabled={!inputValue.trim() || chatLoading}
                  >
                    Send
                  </Button>
                </form>
                {messages.length > 0 && (
                  <Button
                    variant="secondary"
                    onClick={clearMessages}
                    className="w-full mt-2 text-sm"
                  >
                    Clear Conversation
                  </Button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'pitch' && (
            <div className="h-full overflow-y-auto p-4">
              {pitchLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                  <p className="text-neutral-600">Generating your investor pitch...</p>
                </div>
              ) : pitch ? (
                <PitchView pitch={pitch} />
              ) : (
                <div className="text-center py-12">
                  <p className="text-neutral-600 mb-4">
                    No pitch generated yet. Click "Generate Investor Pitch" to get started.
                  </p>
                  <Button onClick={handleGeneratePitch}>
                    Generate Pitch
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
